/**
 * App: Root component with hash-based routing
 * Wraps the app with authentication and provides navigation between pages
 */

import React, { Component, useState, useEffect, useCallback, useMemo } from "react";
import AuthGuard from "./components/AuthGuard";
import CatalogPage from "./pages/CatalogPage";
import PluginDetailPage from "./pages/PluginDetailPage";
import ContributeSkillPage from "./pages/ContributeSkillPage";
import AdminPage from "./pages/AdminPage";
import HelpPage from "./pages/HelpPage";
import type { EditData } from "./pages/ContributeSkillPage";
import { logout, getUser } from "./lib/auth";
import { env, IS_DEV } from "./lib/env";
import { getMarketplaceClient } from "./lib/marketplace-client";
import { fetchSkillTree, fetchSkillBlob, checkAdminStatus, legacyInstallKey } from "./lib/github-proxy-client";
import { reportError } from "./lib/newrelic";
import { trackPageView } from "./lib/gtm";
import type { ExtensionType, MarketplaceEntry, User } from "./types/index";

import { parseHash, routeToHash, type Route } from "./lib/routing";
import { buildParentIndex, computeEffectiveBreadcrumbs } from "./lib/parent-plugin";

function installKeyForPlugin(plugin: MarketplaceEntry): string {
  return plugin.installName
    ?? `${plugin.source}--${plugin.orgOrOwner.toLowerCase()}--${plugin.repoName.toLowerCase()}`;
}

class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught render error:", error, errorInfo);
    reportError(error, "react-error-boundary", {
      componentStack: errorInfo.componentStack ?? "",
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1>Something went wrong</h1>
          <p style={{ color: "#666" }}>{this.state.error.message}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              fontSize: "1rem",
              backgroundColor: "#0078d4",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App(): JSX.Element {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  const [catalog, setCatalog] = useState<MarketplaceEntry[]>([]);
  const [catalogNavKey, setCatalogNavKey] = useState(0);
  const [breadcrumbs, setBreadcrumbs] = useState<MarketplaceEntry[]>([]);

  const [devEmail, setDevEmail] = useState("");
  const [devName, setDevName] = useState("");

  const entraUser = env.REACT_APP_ENTRA_CLIENT_ID ? getUser() : null;
  const devUser: User | null =
    IS_DEV && !entraUser && devEmail
      ? { id: "dev", email: devEmail, name: devName || devEmail }
      : null;
  const user = entraUser ?? devUser;

  const [isAdmin, setIsAdmin] = useState(false);

  const [helpFromSkill, setHelpFromSkill] = useState(false);

  const [editData, setEditData] = useState<EditData | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    getMarketplaceClient().getCatalog()
      .then((c) => setCatalog(c.plugins))
      .catch((err) => {
        console.error("Failed to load catalog for routing:", err);
        reportError(err, "catalog-fetch-routing");
      });
  }, []);

  useEffect(() => {
    if (user?.email) {
      checkAdminStatus().then(setIsAdmin).catch(() => setIsAdmin(false));
    }
  }, [user?.email]);

  const navigate = useCallback((next: Route) => {
    window.location.hash = routeToHash(next);
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const next = parseHash(window.location.hash);
      if (next.page !== "help") setHelpFromSkill(false);
      setRoute(next);
      trackPageView(window.location.hash);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const editInstallKey = route.page === "edit" ? route.installKey : null;

  useEffect(() => {
    if (!editInstallKey) {
      setEditData(null);
      setEditError("");
      return;
    }

    const plugin = catalog.find(
      (p) => installKeyForPlugin(p).toLowerCase() === editInstallKey.toLowerCase(),
    );
    if (!plugin) {
      setEditError("Not found in catalog");
      return;
    }

    let cancelled = false;
    setEditLoading(true);
    setEditError("");

    // github-proxy decodes source/org/repo from the legacy install-key shape;
    // the marketplace's installName (= manifestName post #176) isn't parseable.
    const proxyKey = legacyInstallKey(plugin);

    fetchSkillTree(proxyKey, plugin.lastCommitSha, plugin.skillPath)
      .then(async (tree) => {
        if (cancelled) return;
        const blobs = await Promise.all(
          tree.files.map(async (f) => {
            try {
              const blob = await fetchSkillBlob(proxyKey, f.sha);
              const content = blob.encoding === "base64"
                ? blob.content
                : btoa(new TextEncoder().encode(blob.content).reduce((s, b) => s + String.fromCharCode(b), ""));
              return { name: f.path.split("/").pop()!, path: f.path, content };
            } catch {
              return null;
            }
          }),
        );
        if (cancelled) return;
        const validBlobs = blobs.filter((b): b is NonNullable<typeof b> => b !== null);

        let userKeywords = plugin.keywords;
        const manifestBlob = validBlobs.find((b) => b.path.endsWith("plugin.json"));
        if (manifestBlob) {
          try {
            const manifest = JSON.parse(atob(manifestBlob.content));
            if (Array.isArray(manifest.keywords)) {
              userKeywords = manifest.keywords;
            }
          } catch {
            // fall back to catalog keywords
          }
        }

        setEditData({
          // installKey on editData is sent back to github-proxy as
          // `previousInstallKey` for rename detection, so use the legacy shape.
          installKey: proxyKey,
          skillName: plugin.pluginName,
          description: plugin.description,
          keywords: userKeywords,
          existingFiles: validBlobs,
        });
      })
      .catch((err: unknown) => {
        // Report before the cancelled guard so navigation-away errors
        // (e.g., a github-proxy 5xx during route change) still reach NR.
        // Mirrors the pattern in SkillFiles.tsx skill-tree-fetch.
        reportError(err, "edit-skill-files-load");
        if (cancelled) return;
        setEditData(null);
        setEditError(err instanceof Error ? err.message : "Failed to load files");
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false);
      });

    return () => { cancelled = true; };
  }, [editInstallKey, catalog]);

  const handleSelectPlugin = (plugin: MarketplaceEntry) => {
    setBreadcrumbs([]);
    navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
  };

  const handleBackToCatalog = useCallback(() => {
    setBreadcrumbs([]);
    const catalogRoute: Route = { page: "catalog" };
    setRoute(catalogRoute);
    navigate(catalogRoute);
    setCatalogNavKey((k) => k + 1);
  }, [navigate]);

  const handleBreadcrumbClick = useCallback((plugin: MarketplaceEntry, index: number) => {
    setBreadcrumbs((prev) => prev.slice(0, index));
    navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
  }, [navigate]);

  const navigateToHelp = useCallback(() => {
    setHelpFromSkill(route.page === "detail");
    navigate({ page: "help" });
  }, [route.page, navigate]);

  const replaceFilters = useCallback((query: string, clients: string[], categories: string[], type?: ExtensionType, official?: boolean) => {
    const hash = routeToHash({
      page: "catalog",
      filterClients: clients.length > 0 ? clients : undefined,
      filterCategories: categories.length > 0 ? categories : undefined,
      filterQuery: query || undefined,
      filterType: type,
      filterOfficial: official || undefined,
    });
    window.history.replaceState(null, "", hash);
  }, []);

  const handleLogout = () => {
    logout()
      .catch((error) => console.error("Logout failed:", error))
      .finally(() => window.location.reload());
  };

  const selectedPlugin = (route.page === "detail" || route.page === "edit")
    ? catalog.find((p) => installKeyForPlugin(p).toLowerCase() === route.installKey.toLowerCase()) ?? null
    : null;

  // Resolve the plugin a derived skill came from, so its detail page shows the parent
  // reference regardless of entry point (direct catalog click, deep link). See
  // lib/parent-plugin for how the catalog's skill→plugin link is inverted.
  const parentByChildInstallName = useMemo(() => buildParentIndex(catalog), [catalog]);

  // selectedPlugin is populated for both the detail and edit routes, so this resolves
  // on both; only the detail page consumes parentPlugin (the edit page intentionally
  // ignores it). The lookup is a cheap Map.get, so resolving it unconditionally is fine.
  const parentPlugin = selectedPlugin?.installName
    ? parentByChildInstallName.get(selectedPlugin.installName)
    : undefined;

  const effectiveBreadcrumbs = computeEffectiveBreadcrumbs(breadcrumbs, parentPlugin);

  return (
    <ErrorBoundary>
    <AuthGuard>
      <div style={{ minHeight: "100vh", backgroundColor: "#fafafa" }}>
        {/* Header */}
        <header
          style={{
            backgroundColor: "white",
            borderBottom: "1px solid #e0e0e0",
            padding: "1rem 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1 style={{ margin: 0 }}>
            <button
              onClick={handleBackToCatalog}
              style={{
                background: "none",
                border: "none",
                color: "#0078d4",
                cursor: "pointer",
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ verticalAlign: "middle", marginRight: "0.4rem" }}
              >
                <rect x="3" y="4" width="18" height="16" rx="3" />
                <circle cx="9" cy="12" r="1.5" />
                <circle cx="15" cy="12" r="1.5" />
                <path d="M9 16h6" />
                <path d="M2 10h-1" />
                <path d="M23 10h-1" />
              </svg>
              AI Marketplace
            </button>
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <button
              onClick={() => navigate({ page: "contribute" })}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: "#0078d4",
                color: "white",
                border: "1px solid #0078d4",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Contribute
            </button>
            <button
              onClick={navigateToHelp}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: route.page === "help" ? "#e6f0ff" : "transparent",
                color: route.page === "help" ? "#0078d4" : "#666",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Help
            </button>
            {isAdmin && (
              <button
                onClick={() => navigate({ page: "admin" })}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.9rem",
                  backgroundColor: route.page === "admin" ? "#e8e8e8" : "transparent",
                  color: "#666",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Admin
              </button>
            )}
            {user && (
              <span style={{ color: "#666", fontSize: "0.9rem" }}>
                {user.name || user.email}
              </span>
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: "#f0f0f0",
                color: "#333",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Sign out
            </button>
          </div>
        </header>

        {/* Dev identity (shown in dev mode when no Entra user) */}
        {IS_DEV && !entraUser && (
          <div style={{
            backgroundColor: "#fdf8e8",
            borderBottom: "1px dashed #f0ad4e",
            padding: "0.5rem 2rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.8rem",
            color: "#8a6d3b",
          }}>
            <strong>Dev identity:</strong>
            <input
              type="text"
              value={devName}
              onChange={(e) => setDevName(e.target.value)}
              placeholder="Name"
              style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", border: "1px solid #ddd", borderRadius: "4px", width: "120px" }}
            />
            <input
              type="email"
              value={devEmail}
              onChange={(e) => setDevEmail(e.target.value)}
              placeholder="Email"
              style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", border: "1px solid #ddd", borderRadius: "4px", width: "180px" }}
            />
          </div>
        )}

        {/* Main content */}
        <main style={{ maxWidth: "1200px", margin: "0 auto", padding: "2rem" }}>
          {route.page === "catalog" && (
            <CatalogPage
              key={catalogNavKey}
              onSelectPlugin={handleSelectPlugin}
              initialClients={route.filterClients}
              initialCategories={route.filterCategories}
              initialQuery={route.filterQuery}
              initialType={route.filterType}
              initialOfficial={route.filterOfficial}
              onHelp={navigateToHelp}
              onFiltersChange={replaceFilters}
            />
          )}

          {route.page === "detail" && selectedPlugin && (
            <PluginDetailPage
              plugin={selectedPlugin}
              breadcrumbs={effectiveBreadcrumbs}
              parentPlugin={parentPlugin}
              onBackToCatalog={handleBackToCatalog}
              onBreadcrumbClick={handleBreadcrumbClick}
              onNavigateToPlugin={(plugin) => {
                if (selectedPlugin) setBreadcrumbs((prev) => [...prev, selectedPlugin]);
                navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
              }}
              findPlugin={(installName) => catalog.find((p) => installKeyForPlugin(p) === installName)}
              user={user}
              onEdit={() => navigate({ page: "edit", installKey: installKeyForPlugin(selectedPlugin) })}
              onDelete={() => {
                setCatalog((prev) => prev.filter((p) => installKeyForPlugin(p) !== installKeyForPlugin(selectedPlugin)));
                handleBackToCatalog();
              }}
              onClientClick={(client) => navigate({ page: "catalog", filterClients: [client] })}
              onCategoryClick={(category) => navigate({ page: "catalog", filterCategories: [category] })}
              onKeywordClick={(keyword) => navigate({ page: "catalog", filterQuery: keyword })}
              onHelp={navigateToHelp}
            />
          )}

          {route.page === "detail" && !selectedPlugin && catalog.length > 0 && (
            <div style={{ textAlign: "center", padding: "2rem 0" }}>
              <h2 style={{ color: "#333" }}>Not found</h2>
              <p style={{ color: "#666" }}>The plug-in or skill you're looking for doesn't exist or has been removed.</p>
              <button
                onClick={handleBackToCatalog}
                style={{
                  marginTop: "1rem",
                  padding: "0.5rem 1.5rem",
                  fontSize: "1rem",
                  backgroundColor: "#f0f0f0",
                  color: "#333",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Back to Marketplace
              </button>
            </div>
          )}

          {route.page === "edit" && editLoading && (
            <div style={{ textAlign: "center", padding: "2rem 0" }}>
              <p style={{ color: "#666" }}>Loading details...</p>
            </div>
          )}

          {route.page === "edit" && editError && (
            <div style={{ textAlign: "center", padding: "2rem 0" }}>
              <h2 style={{ color: "#333" }}>Failed to load</h2>
              <p style={{ color: "#666" }}>{editError}</p>
              <button
                onClick={handleBackToCatalog}
                style={{
                  marginTop: "1rem",
                  padding: "0.5rem 1.5rem",
                  fontSize: "1rem",
                  backgroundColor: "#f0f0f0",
                  color: "#333",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Back to Marketplace
              </button>
            </div>
          )}

          {route.page === "edit" && editData && !editLoading && (
            <ContributeSkillPage
              onBack={handleBackToCatalog}
              onViewSkill={(installName) => navigate({ page: "detail", installKey: installName })}
              user={user}
              editData={editData}
            />
          )}

          {route.page === "contribute" && (
            <ContributeSkillPage
              onBack={handleBackToCatalog}
              onViewSkill={(installName) => navigate({ page: "detail", installKey: installName })}
              user={user}
            />
          )}

          {route.page === "help" && (
            <HelpPage
              onBack={helpFromSkill ? () => window.history.back() : handleBackToCatalog}
              backLabel={helpFromSkill ? "Back to Detail" : "Back to Marketplace"}
              onContribute={() => navigate({ page: "contribute" })}
            />
          )}

          {route.page === "admin" && user && (
            <AdminPage onBack={handleBackToCatalog} />
          )}
        </main>

        {/* Footer */}
        <footer
          style={{
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid #e0e0e0",
            padding: "2rem",
            textAlign: "center",
            color: "#999",
            fontSize: "0.9rem",
            marginTop: "2rem",
          }}
        >
          <p style={{ margin: 0 }}>
            AI Marketplace © 2026 • Cox Automotive
          </p>
        </footer>
      </div>
    </AuthGuard>
    </ErrorBoundary>
  );
}
